import { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import api from "../lib/api";
import { toast } from "sonner";
import { TimbraturaGuideDialog, hasSeenTimbraturaGuide } from "../components/TimbraturaGuideDialog";
import { MyWeekCard } from "../components/MyWeekCard";
import {
    Clock, LogIn, LogOut, Hourglass, MapPin, Loader2, CheckCircle2,
    AlertTriangle, UserCircle, HelpCircle, Building2, ClipboardList, ShieldCheck,
} from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
    } catch { return "—"; }
};

const REASON_TITLES = {
    entrata_ritardo: "Entrata in ritardo",
    uscita_anticipata: "Uscita anticipata",
    uscita_posticipata: "Uscita oltre il turno",
};
const reasonMessage = ({ check, type }) => {
    const boundary = type === "in" ? "l'entrata" : "l'uscita";
    const dir = check.code === "uscita_anticipata" ? "anticipo" : "ritardo";
    return `Il turno "${check.activity_name}" prevede ${boundary} alle ${check.expected}: hai timbrato con ${check.delta_min} minuti di ${dir}. Indica la motivazione per l'ufficio.`;
};

export default function TimbraturaPage() {
    const { user, logout } = useAuth();
    const company = useCompany();
    const [now, setNow] = useState(new Date());
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [errorBox, setErrorBox] = useState(null);
    const [successBox, setSuccessBox] = useState(null);
    const [reasonAsk, setReasonAsk] = useState(null);
    const [reasonText, setReasonText] = useState("");
    const [reasonSending, setReasonSending] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [context, setContext] = useState(null);
    const [geoConsent, setGeoConsent] = useState(null);
    const [consentChecked, setConsentChecked] = useState(false);
    const [consentSaving, setConsentSaving] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/presenze/me/status");
            setStatus(data);
        } catch (e) {
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    useEffect(() => {
        if (status?.enabled) {
            api.get("/presenze/geo-consent").then((r) => setGeoConsent(r.data)).catch(() => setGeoConsent(null));
        }
    }, [status?.enabled]);

    const geoAccepted = !!geoConsent && (!geoConsent.required || geoConsent.accepted);

    const acceptConsent = async () => {
        setConsentSaving(true);
        try {
            await api.post("/presenze/geo-consent");
            setGeoConsent((g) => ({ ...g, accepted: true }));
            toast.success("Presa visione registrata");
        } catch {
            toast.error("Errore nella registrazione, riprova");
        } finally {
            setConsentSaving(false);
        }
    };

    useEffect(() => {
        if (status?.enabled && geoAccepted && user?.id && !hasSeenTimbraturaGuide(user.id)) {
            const t = setTimeout(() => setGuideOpen(true), 500);
            return () => clearTimeout(t);
        }
    }, [status?.enabled, geoAccepted, user?.id]);

    const loadContext = useCallback(() => {
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { data } = await api.post("/presenze/me/context", {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    });
                    setContext(data);
                } catch { setContext(null); }
            },
            () => setContext(null),
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
        );
    }, []);

    useEffect(() => {
        if (status?.enabled) loadContext();
    }, [status?.enabled, loadContext]);

    if (user && user.role === "admin") {
        return <Navigate to="/" replace />;
    }

    const getPosition = () => new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject({ code: 0, message: "Geolocalizzazione non supportata da questo dispositivo" });
            return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 15000, maximumAge: 0,
        });
    });

    const doClock = async (type) => {
        setBusy(true);
        setErrorBox(null);
        setSuccessBox(null);
        let pos;
        try {
            pos = await getPosition();
        } catch (e) {
            setBusy(false);
            if (e.code === 1) {
                setErrorBox({
                    title: "Posizione negata",
                    text: "Per timbrare devi consentire l'accesso alla posizione. Apri le impostazioni del browser/telefono, autorizza la posizione per questo sito e riprova.",
                });
            } else {
                setErrorBox({
                    title: "Posizione non disponibile",
                    text: "Impossibile rilevare la tua posizione GPS. Verifica che il GPS sia attivo e riprova all'aperto.",
                });
            }
            return;
        }
        try {
            const { data } = await api.post("/presenze/clock", {
                type,
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
            });
            setSuccessBox({
                type,
                worksite: data.worksite,
                time: data.time_local,
                assigned: data.assigned,
            });
            toast.success(`${type === "in" ? "Entrata" : "Uscita"} registrata — ${data.worksite}, ${data.time_local}`);
            if (data.shift_check) {
                setReasonText("");
                setReasonAsk({ entryId: data.entry.id, check: data.shift_check, type });
            }
            await loadStatus();
            loadContext();
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "out_of_zone") {
                setErrorBox({
                    title: "Fuori zona — timbratura non consentita",
                    text: `Il cantiere più vicino è "${detail.nearest}" a ${detail.distance_m >= 1000 ? (detail.distance_m / 1000).toFixed(1) + " km" : detail.distance_m + " m"} da te. Avvicinati al cantiere e riprova.`,
                });
            } else if (detail && typeof detail === "object" && detail.code === "geo_consent_required") {
                setGeoConsent((g) => (g ? { ...g, accepted: false } : g));
                setErrorBox({ title: "Presa visione richiesta", text: detail.message });
            } else if (detail && typeof detail === "object" && detail.code === "no_sites") {
                setErrorBox({ title: "Nessun cantiere configurato", text: detail.message });
            } else if (typeof detail === "string") {
                setErrorBox({ title: "Timbratura non registrata", text: detail });
            } else {
                setErrorBox({ title: "Errore", text: "Errore imprevisto, riprova tra qualche istante." });
            }
        } finally {
            setBusy(false);
        }
    };

    const sendReason = async () => {
        if (!reasonText.trim()) {
            toast.error("Scrivi una breve motivazione");
            return;
        }
        setReasonSending(true);
        try {
            await api.post(`/presenze/entries/${reasonAsk.entryId}/reason`, { reason: reasonText.trim() });
            toast.success("Motivazione inviata all'ufficio");
            setReasonAsk(null);
        } catch {
            toast.error("Errore nell'invio, riprova");
        } finally {
            setReasonSending(false);
        }
    };

    const dateLabel = now.toLocaleDateString("it-IT", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
    const inService = status?.state === "in";

    return (
        <div
            className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#1F3D6B] via-[#376DB3] to-[#5BAEDB]"
            data-testid="timbratura-page"
        >
            <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#5BAEDB]/30 blur-3xl" />

            <div className="relative z-10 flex min-h-screen flex-col">
                <header className="flex items-center justify-between gap-2 px-4 py-5 sm:px-8">
                    <div className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 sm:text-xs sm:tracking-[0.2em]">
                        {company.ragione_sociale}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setGuideOpen(true)}
                            className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1.5 text-[11px] font-medium text-white backdrop-blur transition-colors hover:bg-white/25 sm:gap-1.5 sm:px-3 sm:text-xs"
                            data-testid="timbratura-guide-btn"
                        >
                            <HelpCircle className="h-3.5 w-3.5" />
                            Guida
                        </button>
                        <Link
                            to="/portale"
                            className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1.5 text-[11px] font-medium text-white backdrop-blur transition-colors hover:bg-white/25 sm:gap-1.5 sm:px-3 sm:text-xs"
                            data-testid="timbratura-portal-link"
                        >
                            <UserCircle className="h-3.5 w-3.5" />
                            Portale
                        </Link>
                        <button
                            type="button"
                            onClick={logout}
                            className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1.5 text-[11px] font-medium text-white backdrop-blur transition-colors hover:bg-white/25 sm:gap-1.5 sm:px-3 sm:text-xs"
                            data-testid="timbratura-logout-btn"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Esci
                        </button>
                    </div>
                </header>

                <TimbraturaGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} userId={user?.id} />

                <main className="flex flex-1 flex-col items-center justify-center px-5 pb-12">
                    <div className="w-full max-w-md">
                        <div className="mb-5 flex justify-center">
                            <div className="rounded-2xl bg-white px-6 py-3 shadow-2xl" data-testid="timbratura-logo">
                                <img
                                    src="/brand/logo-full.jpg"
                                    alt="Splendor Sud"
                                    width={181}
                                    height={67}
                                    className="h-16 w-auto"
                                    style={{ aspectRatio: "1812 / 674" }}
                                />
                            </div>
                        </div>
                        <div className="text-center text-white">
                            <div className="text-sm capitalize text-white/80">{dateLabel}</div>
                            <div className="mt-1 font-mono-numbers text-6xl font-bold tracking-tight" data-testid="timbratura-clock">
                                {pad(now.getHours())}:{pad(now.getMinutes())}
                                <span className="text-3xl text-white/60">:{pad(now.getSeconds())}</span>
                            </div>
                            <div className="mt-1 text-sm text-white/70">Ciao, {user?.name}</div>
                        </div>

                        {loading ? (
                            <div className="mt-10 flex justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-white/70" />
                            </div>
                        ) : !status?.enabled ? (
                            <div className="mt-8 rounded-2xl bg-white/95 p-8 text-center shadow-2xl" data-testid="timbratura-disabled">
                                <Hourglass className="mx-auto h-10 w-10 text-[#376DB3]/60" />
                                <h2 className="mt-3 font-display text-xl font-bold text-slate-800">Modulo non ancora attivo</h2>
                                <p className="mt-2 text-sm text-slate-600">
                                    La timbratura non è ancora stata attivata dall'azienda. Riprova più avanti.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div
                                    className={`mt-8 rounded-2xl p-5 text-center shadow-2xl ${inService ? "bg-emerald-50 ring-2 ring-emerald-400" : "bg-white/95"}`}
                                    data-testid="timbratura-status"
                                >
                                    {inService ? (
                                        <div className="flex items-center justify-center gap-2 text-emerald-700">
                                            <span className="relative flex h-3 w-3">
                                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                                            </span>
                                            <span className="text-sm font-bold">
                                                IN SERVIZIO presso {status.current?.worksite_name} dalle {fmtTime(status.current?.at)}
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="text-sm font-bold text-slate-500">FUORI SERVIZIO</div>
                                    )}
                                </div>

                                {context?.found && (
                                    <div className="mt-4 rounded-2xl bg-white/95 p-4 shadow-2xl" data-testid="timbratura-context">
                                        <div className="flex items-start gap-2.5">
                                            <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#376DB3]/10 text-[#376DB3]">
                                                <Building2 className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0">
                                                {context.worksite.client_name && (
                                                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400" data-testid="timbratura-context-client">
                                                        {context.worksite.client_name}
                                                    </div>
                                                )}
                                                <div className="truncate text-sm font-bold text-slate-800" data-testid="timbratura-context-worksite">
                                                    {context.worksite.name}
                                                </div>
                                                <div className={`text-xs font-medium ${context.within ? "text-emerald-600" : "text-amber-600"}`} data-testid="timbratura-context-distance">
                                                    {context.within
                                                        ? "Sei nell'area del cantiere"
                                                        : `Cantiere più vicino, a ${context.distance_m >= 1000 ? `${(context.distance_m / 1000).toFixed(1)} km` : `${context.distance_m} m`} da te`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2.5 flex items-start gap-2 border-t border-slate-100 pt-2.5" data-testid="timbratura-context-activity">
                                            <ClipboardList className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                                            {context.activity ? (
                                                <div className="text-sm text-slate-600">
                                                    Attività {context.activity.status === "in_corso" ? "in corso" : "prossima"}:{" "}
                                                    <b className="text-slate-800">{context.activity.activity_name}</b>
                                                    {" · "}turno <span className="font-mono-numbers font-semibold">{context.activity.start}–{context.activity.end}</span>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-slate-500">Nessuna attività prevista per te oggi in questo cantiere</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5">
                                    {inService ? (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => doClock("out")}
                                            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-red-600 py-6 text-xl font-bold text-white shadow-2xl transition-transform active:scale-[0.98] disabled:opacity-60"
                                            data-testid="timbratura-clock-out-btn"
                                        >
                                            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <LogOut className="h-6 w-6" />}
                                            {busy ? "Rilevo la posizione…" : "TIMBRA USCITA"}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => doClock("in")}
                                            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 py-6 text-xl font-bold text-white shadow-2xl transition-transform active:scale-[0.98] disabled:opacity-60"
                                            data-testid="timbratura-clock-in-btn"
                                        >
                                            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <LogIn className="h-6 w-6" />}
                                            {busy ? "Rilevo la posizione…" : "TIMBRA ENTRATA"}
                                        </button>
                                    )}
                                    <div className="mt-2 flex items-center justify-center gap-1 text-[11px] text-white/70">
                                        <MapPin className="h-3 w-3" />
                                        La timbratura richiede la posizione GPS
                                    </div>
                                </div>

                                {successBox && (
                                    <div className="mt-4 rounded-xl bg-emerald-500/95 p-4 text-white shadow-xl" data-testid="timbratura-success">
                                        <div className="flex items-center gap-2 font-bold">
                                            <CheckCircle2 className="h-5 w-5" />
                                            {successBox.type === "in" ? "Entrata" : "Uscita"} registrata alle {successBox.time}
                                        </div>
                                        <div className="mt-1 text-sm text-white/90">
                                            Cantiere: {successBox.worksite}
                                            {!successBox.assigned && " (fuori assegnazione — sostituzione)"}
                                        </div>
                                    </div>
                                )}
                                {errorBox && (
                                    <div className="mt-4 rounded-xl bg-red-600/95 p-4 text-white shadow-xl" data-testid="timbratura-error">
                                        <div className="flex items-center gap-2 font-bold">
                                            <AlertTriangle className="h-5 w-5" />
                                            {errorBox.title}
                                        </div>
                                        <div className="mt-1 text-sm text-white/90">{errorBox.text}</div>
                                    </div>
                                )}

                                {status?.today_entries?.length > 0 && (
                                    <div className="mt-6 rounded-2xl bg-white/95 p-4 shadow-2xl" data-testid="timbratura-today-list">
                                        <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                                            <Clock className="h-3.5 w-3.5" /> Timbrature di oggi
                                        </div>
                                        <ul className="divide-y divide-slate-100">
                                            {status.today_entries.map((e) => (
                                                <li key={e.id} className="flex items-center justify-between py-2 text-sm">
                                                    <span className={`inline-flex items-center gap-1.5 font-semibold ${e.type === "in" ? "text-emerald-700" : "text-red-600"}`}>
                                                        {e.type === "in" ? <LogIn className="h-3.5 w-3.5" /> : <LogOut className="h-3.5 w-3.5" />}
                                                        {e.type === "in" ? "Entrata" : "Uscita"}
                                                    </span>
                                                    <span className="text-slate-600">{e.worksite_name}</span>
                                                    <span className="font-mono-numbers font-semibold text-slate-700">{fmtTime(e.at)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                        {status && <MyWeekCard variant="timbratura" className="mt-6" />}
                    </div>
                </main>
            </div>

            {reasonAsk && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center" data-testid="timbratura-reason-dialog">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="flex items-center gap-2 text-amber-600">
                            <AlertTriangle className="h-6 w-6" />
                            <h3 className="font-display text-lg font-bold text-slate-800" data-testid="timbratura-reason-title">
                                {REASON_TITLES[reasonAsk.check.code] || "Timbratura fuori orario"}
                            </h3>
                        </div>
                        <p className="mt-2 text-sm text-slate-600">{reasonMessage(reasonAsk)}</p>
                        <textarea
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            rows={3}
                            placeholder="Es. il cliente ha chiesto di finire prima, traffico, emergenza…"
                            className="mt-3 w-full rounded-md border border-slate-200 p-2 text-sm"
                            data-testid="timbratura-reason-input"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                type="button"
                                onClick={() => setReasonAsk(null)}
                                className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-500 hover:bg-slate-50"
                                data-testid="timbratura-reason-later"
                            >
                                Più tardi
                            </button>
                            <button
                                type="button"
                                onClick={sendReason}
                                disabled={reasonSending}
                                className="flex-1 rounded-xl bg-[#376DB3] py-2.5 text-sm font-bold text-white hover:bg-[#2c5a96] disabled:opacity-60"
                                data-testid="timbratura-reason-send"
                            >
                                {reasonSending ? "Invio…" : "Invia motivazione"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {status?.enabled && geoConsent?.required && !geoConsent.accepted && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 sm:items-center" data-testid="geo-consent-dialog">
                    <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="flex items-center gap-2 text-[#376DB3]">
                            <ShieldCheck className="h-6 w-6" />
                            <h3 className="font-display text-lg font-bold text-slate-800">Informativa geolocalizzazione</h3>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="geo-consent-summary">
                            {geoConsent.summary}
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                            L'informativa completa è disponibile tra i tuoi documenti nel Portale Dipendente
                            (&ldquo;Informativa geolocalizzazione timbratura&rdquo;).
                        </p>
                        <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-xl bg-slate-50 p-3">
                            <input
                                type="checkbox"
                                checked={consentChecked}
                                onChange={(e) => setConsentChecked(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#376DB3]"
                                data-testid="geo-consent-checkbox"
                            />
                            <span className="text-sm font-medium text-slate-700">
                                Dichiaro di aver letto e compreso l'informativa sul trattamento dei dati di geolocalizzazione
                            </span>
                        </label>
                        <button
                            type="button"
                            onClick={acceptConsent}
                            disabled={!consentChecked || consentSaving}
                            className="mt-4 w-full rounded-xl bg-[#376DB3] py-3 text-sm font-bold text-white hover:bg-[#2c5a96] disabled:opacity-50"
                            data-testid="geo-consent-accept-btn"
                        >
                            {consentSaving ? "Registrazione…" : "Confermo la presa visione"}
                        </button>
                        <p className="mt-2 text-center text-[11px] text-slate-400">La conferma viene registrata con data e ora</p>
                    </div>
                </div>
            )}
        </div>
    );
}
